import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, Typography,
  FormControlLabel, Checkbox, FormGroup, Box,
  Autocomplete, Chip, Divider
} from '@mui/material';
import { toast } from 'react-toastify';
import { apigetPublic, apiputFormData } from '../../service/api';
import ReviewsEditor, { blocksToReviews, reviewsToBlocks } from '../../components/lopage/ReviewsEditor';
import { unwrapToStringArray, uniqueStrings } from '../../utils/stringList';

const BASIC_FIELDS = [
  ['firstName', 'First Name'],
  ['lastName', 'Last Name'],
  ['title', 'Title'],
  ['email', 'Email'],
  ['phone', 'Phone'],
  ['nmlsId', 'NMLS ID'],
];

const SOCIAL_FIELDS = [
  ['linkedinUrl', 'LinkedIn URL'],
  ['facebook', 'Facebook URL'],
  ['instagram', 'Instagram URL'],
  ['tiktok', 'TikTok URL'],
  ['twitter', 'Twitter URL'],
];

// Plain text fields this form owns. Only these are sent on save, so fields the
// form doesn't edit (bio, branches, seo, imageUrl...) are left untouched.
const TEXT_FIELDS = [
  ...BASIC_FIELDS.map(([name]) => name),
  'slug', 'applyLink', 'googleReviewLink', 'additionalLicensing',
  ...SOCIAL_FIELDS.map(([name]) => name),
];

const LoanOfficerEditModal = ({ open, onClose, loanOfficer, onUpdate }) => {
  const [formData, setFormData] = useState({});
  const [headshotFile, setHeadshotFile] = useState(null);
  const [reviewBlocks, setReviewBlocks] = useState(() => reviewsToBlocks([]));

  // Dynamic lists
  const [statesList, setStatesList] = useState([]);

  // Load states
  useEffect(() => {
    const load = async () => {
      const s = await apigetPublic('states/search');
      if (s?.status === 200) setStatesList(s.data.result || []);
    };
    load();
  }, []);

  // ---------------------------------------------
  // FIX #1: Load LO into state, but UNWRAP arrays.
  // Re-runs each time the dialog opens, so Cancel discards unsaved edits.
  // ---------------------------------------------
  useEffect(() => {
    if (!loanOfficer || !open) return;

    setFormData({
      ...loanOfficer,
      states: uniqueStrings(unwrapToStringArray(loanOfficer.states)),
      stateOptionsSelected: uniqueStrings(unwrapToStringArray(loanOfficer.stateOptionsSelected)),
      groups: uniqueStrings(unwrapToStringArray(loanOfficer.groups)),
    });
    setReviewBlocks(reviewsToBlocks(loanOfficer.reviews));
    setHeadshotFile(null);
  }, [loanOfficer, open]);

  // Handlers
  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleStateCheckboxChange = (e) => {
    const { value, checked } = e.target;
    const current = uniqueStrings(formData.states);

    const updated = checked
      ? uniqueStrings([...current, value])
      : current.filter(x => x !== value);

    setFormData(prev => ({ ...prev, states: updated }));
  };

  const handleStateOptionCheckbox = (option, stateName, checked) => {
    const key = `${stateName}::${option}`;
    const current = uniqueStrings(formData.stateOptionsSelected);

    const updated = checked
      ? uniqueStrings([...current, key])
      : current.filter(x => x !== key);

    setFormData(prev => ({ ...prev, stateOptionsSelected: updated }));
  };

  // ---------------------------------------------
  // FIX #2: Submit arrays as repeated multipart keys
  // (NOT JSON.stringify arrays)
  // ---------------------------------------------
  const handleSubmit = async () => {
    const { reviews, error } = blocksToReviews(reviewBlocks);
    if (error) {
      toast.error(error);
      return;
    }

    try {
      const data = new FormData();

      TEXT_FIELDS.forEach((key) => data.append(key, formData[key] ?? ''));

      // append arrays as repeated keys; a lone "" tells the server the list
      // was emptied (otherwise it would keep the old values)
      const appendArray = (key, arr) => {
        const list = uniqueStrings(arr);
        if (list.length === 0) data.append(key, '');
        list.forEach(item => data.append(key, item));
      };

      appendArray('states', formData.states);
      appendArray('stateOptionsSelected', formData.stateOptionsSelected);
      appendArray('groups', formData.groups);

      data.append('reviews', JSON.stringify(reviews));

      if (headshotFile) data.append('headshot', headshotFile);

      const result = await apiputFormData(`loanofficer/${loanOfficer.slug}`, data);

      if (result?.status === 200) {
        toast.success('Loan Officer updated!');
        onUpdate?.();
        onClose?.();
      } else {
        toast.error('Error updating officer');
      }
    } catch (err) {
      toast.error('Unexpected error while updating.');
      console.error(err);
    }
  };

  if (!loanOfficer) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Loan Officer</DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* HEADSHOT */}
          <Grid item xs={12}>
            <Typography variant="subtitle2">Current Headshot</Typography>
            {formData.imageUrl && (
              <img
                src={formData.imageUrl}
                alt="Headshot"
                style={{
                  width: 120,
                  height: 120,
                  objectFit: 'cover',
                  borderRadius: 8,
                  marginBottom: 10
                }}
              />
            )}

            <Typography variant="subtitle2">Upload New Headshot</Typography>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setHeadshotFile(e.target.files?.[0] || null)}
            />
          </Grid>

          {/* BASIC FIELDS */}
          {BASIC_FIELDS.map(([field, label]) => (
            <Grid item xs={12} sm={6} key={field}>
              <TextField
                fullWidth
                name={field}
                label={label}
                value={formData[field] || ''}
                onChange={handleChange}
              />
            </Grid>
          ))}

          {/* SLUG */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="slug"
              label="Slug"
              value={formData.slug || ''}
              onChange={handleChange}
            />
          </Grid>

          {/* LINKS */}
          {[
            ['applyLink', 'Apply Now Link'],
            ['googleReviewLink', 'Google Review Link'],
          ].map(([name, label]) => (
            <Grid item xs={12} sm={6} key={name}>
              <TextField
                fullWidth
                name={name}
                label={label}
                value={formData[name] || ''}
                onChange={handleChange}
              />
            </Grid>
          ))}

          {/* GROUPS */}
          <Grid item xs={12}>
            <Typography variant="h6">Groups</Typography>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={formData.groups || []}
              onChange={(e, newValue) =>
                setFormData(prev => ({ ...prev, groups: uniqueStrings(newValue) }))
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    key={`${option}-${index}`}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Type a group and press Enter"
                  helperText="Example: veterans, first-time-buyers, investors"
                />
              )}
            />
          </Grid>

          {/* LICENSING FOR GA */}
          {formData.states?.includes('Georgia') && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="additionalLicensing"
                label="Additional Licensing (for GA)"
                value={formData.additionalLicensing || ''}
                onChange={handleChange}
              />
            </Grid>
          )}

          {/* STATES */}
          <Grid item xs={12}>
            <Typography variant="h6">States</Typography>
            <FormGroup>
              {statesList.map(state => {
                const isSelected = (formData.states || []).includes(state.name);

                return (
                  <Box key={state._id} sx={{ mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isSelected}
                          value={state.name}
                          onChange={handleStateCheckboxChange}
                        />
                      }
                      label={`${state.name} (${state.code})`}
                    />

                    {isSelected && state.additionalOptions?.length > 0 && (
                      <Box sx={{ ml: 5 }}>
                        {state.additionalOptions.map(option => {
                          const key = `${state.name}::${option}`;
                          const isChecked = (formData.stateOptionsSelected || []).includes(key);

                          return (
                            <FormControlLabel
                              key={option}
                              control={
                                <Checkbox
                                  checked={isChecked}
                                  onChange={(e) =>
                                    handleStateOptionCheckbox(option, state.name, e.target.checked)
                                  }
                                />
                              }
                              label={option}
                            />
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </FormGroup>
          </Grid>

          {/* REVIEWS */}
          <Grid item xs={12}>
            <Divider sx={{ mb: 2 }} />
            <ReviewsEditor blocks={reviewBlocks} onChange={setReviewBlocks} />
          </Grid>

          {/* SOCIAL MEDIA (kept for later; not shown on the LO page) */}
          <Grid item xs={12}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h6">Social Media</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Not currently shown on the LO page.
            </Typography>
          </Grid>
          {SOCIAL_FIELDS.map(([name, label]) => (
            <Grid item xs={12} sm={6} key={name}>
              <TextField
                fullWidth
                name={name}
                label={label}
                value={formData[name] || ''}
                onChange={handleChange}
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>Save Changes</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoanOfficerEditModal;
